import React, { useEffect, useMemo, useState } from "react";
import {
    Box,
    Typography,
    Paper,
    ToggleButton,
    ToggleButtonGroup,
    FormControlLabel,
    Switch,
    CircularProgress,
    Alert,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { fetchWithAuth } from "../../utils/api";

const AdminViewEvents = () => {
    const [events, setEvents] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");

    const [sortMode, setSortMode] = useState("recent");
    const [showDetails, setShowDetails] = useState(false);

    const theme = useTheme();

    useEffect(() => {
        const loadEvents = async () => {
            try {
                const res = await fetchWithAuth("/admin/events/view");
                setEvents(Array.isArray(res) ? res : []);
            } catch (err) {
                setError("Failed to load events.");
            } finally {
                setLoading(false);
            }
        };

        loadEvents();
    }, []);

    const sortedEvents = useMemo(() => {
        const copy = [...events];

        if (sortMode === "alphabetical") {
            copy.sort((a, b) => a.name.localeCompare(b.name));
        } else {
            copy.sort(
                (a, b) =>
                    new Date(b.start_date || 0) -
                    new Date(a.start_date || 0)
            );
        }

        return copy;
    }, [events, sortMode]);

    const formatDate = (dateString) =>
        new Date(dateString).toLocaleDateString("en-US", {
            month: "long",
            day: "numeric",
            year: "numeric",
        });

    return (
        <Box sx={{ maxWidth: 1000, mx: "auto", p: 2 }}>
            <Typography
                variant="h5"
                sx={{
                    mb: 2,
                    fontWeight: "bold",
                }}
            >
                View Events
            </Typography>

            <Paper sx={{ p: 2, mb: 2 }}>
                <Box
                    display="flex"
                    flexWrap="wrap"
                    gap={2}
                    alignItems="center"
                    justifyContent="space-between"
                >
                    <ToggleButtonGroup
                        exclusive
                        value={sortMode}
                        onChange={(e, value) => {
                            if (value) setSortMode(value);
                        }}
                        size="small"
                    >
                        <ToggleButton value="recent" sx={{ pl: 1, pr: 1 }}>
                            Most Recent
                        </ToggleButton>

                        <ToggleButton value="alphabetical" sx={{ pl: 1, pr: 1 }}>
                            Alphabetical
                        </ToggleButton>
                    </ToggleButtonGroup>

                    <FormControlLabel
                        control={
                            <Switch
                                checked={showDetails}
                                onChange={(e) =>
                                    setShowDetails(e.target.checked)
                                }
                            />
                        }
                        label="Show Details"
                    />
                </Box>
            </Paper>

            {loading && (
                <Box textAlign="center" mt={4}>
                    <CircularProgress />
                </Box>
            )}

            {!loading && error && (
                <Alert severity="error">
                    {error}
                </Alert>
            )}

            {!loading &&
                !error &&
                sortedEvents.map((event) => (
                    <Paper
                        key={event.id}
                        sx={{
                            p: 2,
                            mb: 2,
                        }}
                    >
                        {showDetails ? (
                            <>
                                <Typography
                                    variant="subtitle1"
                                    fontWeight="bold"
                                    sx={{
                                        color: theme.palette.accent.main
                                    }}
                                >
                                    {event.name}{" "}(
                                    {event.start_date ? formatDate(event.start_date) : "-"}
                                    {" - "}
                                    {event.end_date ? formatDate(event.end_date) : "-"}
                                    )
                                </Typography>

                                <Typography
                                    variant="body2"
                                >
                                    • Items: {event.item_count}
                                </Typography>

                                {event.description && (
                                    <Typography
                                        variant="body2"
                                    >
                                        • {event.description}
                                    </Typography>
                                )}
                            </>
                        ) : (
                            <Typography>
                                {event.name} ({event.item_count} items)
                            </Typography>
                        )}
                    </Paper>
                ))}

            {!loading &&
                !error &&
                sortedEvents.length === 0 && (
                    <Alert severity="info">
                        No events found.
                    </Alert>
                )}
        </Box>
    );
};

export default AdminViewEvents;